import moment from 'moment-timezone';
import * as Dates from '../Dates';
import * as Units from '../Units';
import * as Weather from '../Weather';

export const extractReportData = (
  data: Record<string, number[]>,
  index: number,
  year: number,
  month: number,
  day?: number,
) => {
  if (typeof day !== 'undefined') {
    // year, month and day are all specified
    if (
      Object.keys(data).includes(
        `${year}/${month.toString().padStart(2, '0')}/${day
          .toString()
          .padStart(2, '0')}`,
      )
    ) {
      return data[
        `${year}/${month.toString().padStart(2, '0')}/${day
          .toString()
          .padStart(2, '0')}`
      ][index];
    }
  } else {
    // only year and month are specified
    if (
      Object.keys(data).includes(`${year}/${month.toString().padStart(2, '0')}`)
    ) {
      return data[`${year}/${month.toString().padStart(2, '0')}`][index];
    }
  }
  return null;
};

export const notNull = (data: Record<string, number[]>, index: number) =>
  Object.entries(data)
    .filter((e) => e[1][index] !== null)
    .reduce(
      (obj, d) => {
        obj[d[0]] = d[1];
        return obj;
      },
      {} as Record<string, number[]>,
    );

export const NotNullTuple = (
  data: Record<string, number[]>,
  index1: number,
  index2: number,
) =>
  Object.entries(data)
    .filter((e) => e[1][index1] !== null && e[1][index2] !== null)
    .reduce(
      (obj, d) => {
        obj[d[0]] = d[1];
        return obj;
      },
      {} as Record<string, number[]>,
    );

export const getMax = (data: Record<string, number[]>, index: number) =>
  Object.keys(notNull(data, index)).length > 0
    ? Math.max(
        ...Object.values(notNull(data, index)).map((values) => values[index]),
      )
    : null;

export const getMin = (data: Record<string, number[]>, index: number) =>
  Object.keys(notNull(data, index)).length > 0
    ? Math.min(
        ...Object.values(notNull(data, index)).map((values) => values[index]),
      )
    : null;

export const getExtremeDate = (
  func: (data: Record<string, number[]>, index: number) => number,
  data: Record<string, number[]>,
  index: number,
  timezone: string,
) => {
  if (Object.keys(data).length > 0) {
    const extremeValue = func(data, index);
    if (extremeValue !== null) {
      // We want to find the latest occurence of extreme values (=> reverse to start from the most recent)
      const extremeDate = Object.keys(data)
        .reverse()
        .find((ts) => data[ts][index] === extremeValue);
      return Dates.formatDate(
        moment.tz(extremeDate, 'YYYY/MM/DD', timezone).unix(),
        timezone,
        'LL',
      );
    }
  }
  return null;
};

export const getMaxDate = (
  data: Record<string, number[]>,
  index: number,
  timezone: string,
) => getExtremeDate(getMax, data, index, timezone);

export const getMinDate = (
  data: Record<string, number[]>,
  index: number,
  timezone: string,
) => getExtremeDate(getMin, data, index, timezone);

export const getSum = (data: Record<string, number[]>, index: number) =>
  Object.keys(notNull(data, index)).length > 0
    ? Object.values(notNull(data, index)).reduce((a, b) => a + b[index], 0)
    : null;

export const getAvg = (data: Record<string, number[]>, index: number) => {
  const notNullData = notNull(data, index);
  if (Object.values(notNullData).length > 0) {
    return getSum(notNullData, index) / Object.keys(notNullData).length;
  }
  return null;
};

export const countLessThan = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) =>
  Object.values(notNull(data, index)).filter(
    (values) => values[index] < threshold,
  ).length;

export const countLessThanOrEqual = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) =>
  Object.values(notNull(data, index)).filter(
    (values) => values[index] <= threshold,
  ).length;

export const countGreaterThan = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) => Object.values(data).filter((values) => values[index] > threshold).length;

export const countGreaterThanOrEqual = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) => Object.values(data).filter((values) => values[index] >= threshold).length;

export const countEqual = (
  data: Record<string, number[]>,
  index: number,
  threshold: number,
) => Object.values(data).filter((values) => values[index] === threshold).length;

export const countBetweenValues = (
  data: Record<string, number[]>,
  index: number,
  thresholdLow: number,
  thresholdHigh: number,
) =>
  Object.values(notNull(data, index)).filter(
    (values) => values[index] <= thresholdHigh && values[index] > thresholdLow,
  ).length;

export const getMaxMagnitude = (
  data: Record<string, number[]>,
  units: Units.Units,
  metric: string,
  index1: number,
  index2: number,
) =>
  Math.max(
    ...Object.values(NotNullTuple(data, index1, index2)).map((values) =>
      Math.abs(
        (Units.convertValue(values[index1], metric, units) as number) -
          (Units.convertValue(values[index2], metric, units) as number),
      ),
    ),
  );

export const getMinMagnitude = (
  data: Record<string, number[]>,
  units: Units.Units,
  metric: string,
  index1: number,
  index2: number,
) =>
  Math.min(
    ...Object.values(NotNullTuple(data, index1, index2)).map((values) =>
      Math.abs(
        (Units.convertValue(values[index1], metric, units) as number) -
          (Units.convertValue(values[index2], metric, units) as number),
      ),
    ),
  );

export const getHeatingDegreeDaySum = (
  data: Record<string, number[]>,
  referenceTemperature: number,
  index1: number,
  index2: number,
  costic = false,
) =>
  Weather.sumHeatingDegreeDay(
    Object.values(data).map((values) => [values[index1], values[index2]]),
    referenceTemperature,
    costic,
  );

export const getCoolingDegreeDaySum = (
  data: Record<string, number[]>,
  referenceTemperature: number,
  index1: number,
  index2: number,
  costic = false,
) =>
  Weather.sumCoolingDegreeDay(
    Object.values(data).map((values) => [values[index1], values[index2]]),
    referenceTemperature,
    costic,
  );

/** One class of the monthly report's rain-day counts, as its label states it. */
export type RainDayBand =
  | {kind: 'equal'; value: string}
  | {kind: 'between'; low: string; high: string}
  | {kind: 'above'; value: string};

export type RainDayClass = {
  /** The thresholds with their unit, e.g. "0.04in", for the row's label. */
  band: RainDayBand;
  /** How many days of `data` (daily rain in mm at `index`) fall in it. */
  count: (data: Record<string, number[]>, index: number) => number;
};

const MM_PER_INCH = 25.4;

/**
 * The class edges, in mm: a single 0.1 mm tip, then 0-1, 1-5, 5-10 and over
 * 10, each band (low, high], so every rainy day falls in exactly one (a
 * 10.0 mm day once counted as both "5 < Rain ≤ 10" and "Rain ≥ 10").
 */
const RAIN_BANDS = [
  [0, 1],
  [1, 5],
  [5, 10],
] as const;
const RAIN_TRACE_MM = 0.1;
const RAIN_HEAVY_MM = 10;

/** A rain edge in mm, in the user's unit: two decimals in inches. */
const rainEdge = (mm: number, units: Units.Units) =>
  units.RR === Units.INCH
    ? (mm / MM_PER_INCH).toFixed(2)
    : Units.convertValueToString(mm, 'Rain', units);

/**
 * A rain threshold the counts apply in mm, as a label states it: "0.39 in"
 * for 10 mm (not the raw "0.394 in"), "10.0 mm".
 */
export const rainThreshold = (mm: number, units: Units.Units) =>
  `${rainEdge(mm, units)} ${Units.getRainUnitLabel(units)}`;

/**
 * A temperature threshold the counts apply in °C, as a label states it, to
 * the whole degree: 0 °C is "32 °F", 25 °C "77 °F".
 */
export const temperatureThreshold = (celsius: number, units: Units.Units) =>
  `${Units.convertValueToString(celsius, 'Temperature', units, 0)} ${Units.getTemperatureUnitLabel(units)}`;

/**
 * The rain-day classes of the monthly report. The counts are the metric rule
 * for every user, so a station reports the same facts in either unit
 * system; only the labels follow the rain unit. In inches the edges read at
 * two decimals (1 mm "0.04in", 10 mm "0.39in"), and the single-tip row keeps
 * its 0.1 mm with the inch reading beside it: converted, it was "0.004in".
 */
export const rainDayClasses = (units: Units.Units): RainDayClass[] => {
  const label = Units.getRainUnitLabel(units);
  const inches = units.RR === Units.INCH;
  const text = (mm: number) => `${rainEdge(mm, units)}${label}`;
  return [
    {
      band: {
        kind: 'equal',
        value: inches
          ? `${RAIN_TRACE_MM}mm (< 0.01${label})`
          : text(RAIN_TRACE_MM),
      },
      count: (data, index) => countEqual(data, index, RAIN_TRACE_MM),
    },
    ...RAIN_BANDS.map(([low, high]) => ({
      band: {kind: 'between', low: text(low), high: text(high)} as const,
      count: (data: Record<string, number[]>, index: number) =>
        countBetweenValues(data, index, low, high),
    })),
    {
      band: {kind: 'above', value: text(RAIN_HEAVY_MM)},
      count: (data, index) => countGreaterThan(data, index, RAIN_HEAVY_MM),
    },
  ];
};

/** The pressure the report's high/low day counts split at, in hPa. */
export const PRESSURE_REFERENCE_HPA = 1013;

/**
 * 1013 hPa in the user's pressure unit, with it: "1013 mb", "29.91 inHg",
 * "760 mmHg". The count itself stays at 1013 hPa.
 */
export const pressureReference = (units: Units.Units) =>
  `${Units.convertValueToString(
    PRESSURE_REFERENCE_HPA,
    'SLP',
    units,
    units.SLP === Units.IN_HG ? 2 : 0,
  )} ${Units.getPressureUnitLabel(units)}`;
